import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  INewCardStateCompleted,
  INewCompletedAccountCardsState,
} from 'src/app/api/interfaces/card.interface';
import {
  craftBuildingNames,
  IApiCityResponse,
  IBuildingCraft,
  IBuildingHouse,
  ICraftCharacterExperience,
} from 'src/app/api/interfaces/city.interface';
import {
  buildingsType,
  IApiConfigurationResponse,
  IEnergyRecipeConfiguration,
  IRecipeConfigurations,
  recipeConfigurationType,
  specialityType,
} from 'src/app/api/interfaces/configuration.interface';
import { IPubResultUI } from 'src/app/api/interfaces/pub.interface';
import {
  IBuildingCraftingTimers,
  IBuildingCrafting,
  IHouseBuildingWithSlot,
  ICraftingBuilding,
  IComletedRecipeSpecialityRequairment,
  ICrafting,
  ICompletedRecipe,
} from 'src/app/api/interfaces/crafting.interface';
import {
  ICharacterCompleted,
  ICompletedCharacterSlots,
  ICompletedCharacterSpeciality,
} from 'src/app/store/characterState/characterState.reducer';
import { IMainState } from 'src/app/store/mainState/mainState.reducer';
import {
  ICharacterCompletedWithPreviewSpecialityExp,
  ICraftedCards,
} from '../modal-components/crafting/shared/crafting-base/crafting-base.component';
import { IEnergyCharacterSlot } from '../modal-components/home/home.component';
import { ICompletedHouseRecipe } from '../modal-components/home/slices/home-recipes-list/home-recipes-list.component';
import { ICraftTypes } from './game-modal.service';

@Injectable({
  providedIn: 'root',
})
export class CraftingService {
  public craftingType: BehaviorSubject<ICraftTypes> =
    new BehaviorSubject<ICraftTypes>({
      displayName: 'lake',
      typeName: { building: 'lake', system: 'LAKE' },
    });
  public craftTimersInProcess: BehaviorSubject<IBuildingCraftingTimers> =
    new BehaviorSubject<IBuildingCraftingTimers>({
      lake: null,
      field: null,
      greenhouse: null,
      laboratory: null,
      restaurant: null,
    });
  public craftingBuildings: BehaviorSubject<IBuildingCrafting> =
    new BehaviorSubject<IBuildingCrafting>({
      house: null,
      lake: null,
      field: null,
      greenhouse: null,
      laboratory: null,
      restaurant: null,
    });
  public updateCharacterStatus: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  constructor() {}

  public createHomeRecipeList(
    building: IHouseBuildingWithSlot,
    recipes: IEnergyRecipeConfiguration[],
    userCards: INewCardStateCompleted
  ): ICompletedHouseRecipe[] {
    return recipes.map((recipe) => {
      return {
        checks: {
          cards: this.getUserRequairedRecipeCards(
            recipe,
            userCards.completedCards
          ),
          level: recipe.level <= building.building.level,
          maxCharacter:
            recipe.maxCharacters >=
              building.building.lastCharacterIdsUsed.length &&
            building.building.lastCharacterIdsUsed.length > 0,
        },
        requaired: {
          cards: this.getUserRequairedRecipeCardsArray(recipe, userCards),
          characterIds: building.lastSelectedCharactersSlot.map(
            (characterSlot) => characterSlot?.character?.id
          ),
        },
        recipeConfig: recipe,
      };
    });
  }
  public calculateNewEnergyProgress(
    newQuantity: number,
    characterSlot: IEnergyCharacterSlot
  ): IEnergyCharacterSlot {
    if (characterSlot.character.character?.energy) {
      const resultInPercent =
        ((newQuantity + characterSlot.character.character.energy.quantity) /
          characterSlot.character.character.energy.capacity) *
        100;
      return {
        ...characterSlot,
        energy: {
          quantity: newQuantity,
          quantityInPercent: resultInPercent,
        },
      };
    } else {
      return characterSlot;
    }
  }
  public createCraftList(
    configState: IApiConfigurationResponse,
    craftBuildingState: IBuildingCrafting,
    craftingType: ICraftTypes,
    cityState: IApiCityResponse,
    mainState: IMainState,
    characterState: ICompletedCharacterSlots[],
    type: 'COMMON' | 'TRANSFORMATION' | 'FISHING'
  ): ICompletedRecipe[] {
    const recipes = configState.resourceRecipeConfigurations
      .filter(
        (recipe) =>
          recipe.building === craftingType.typeName.system &&
          recipe.type === type
      )
      .sort(
        (recipeA, recipeB) => recipeA.sequenceNumber - recipeB.sequenceNumber
      );
    const pickedCharacter = this.findCharacterInState(
      characterState,
      craftBuildingState,
      craftingType
    )?.character;
    const buiildingLevel =
      craftBuildingState[craftingType.typeName.building]?.building.level;
    if (pickedCharacter && pickedCharacter.specialities.length > 0) {
      const complepedRecipes = recipes.map((recipe) => {
        const specialities = this.getCompletedRecipeSpecialityRequaired(
          recipe,
          pickedCharacter
        );
        const booster = this.getUserBooster(
          recipe,
          mainState.cards.completedCards
        );
        const energy = this.getCompletedRecipeEnergyRequaired(
          recipe,
          pickedCharacter
        );
        const craftTime = this.getRecipeCraftTime(recipe, pickedCharacter);
        const cost = this.getRecipeCost(
          recipe,
          {
            cityTax: cityState.cityTaxRate.craft,
            systemTax: configState.systemConfiguration.taxRate,
          },
          1
        );
        const userBalance = mainState.account.balances.find(
          (balance) => balance.currency === 'GOLD'
        )?.quantity;
        const requairmentCardsUserHave = this.getUserRequairedRecipeCardsArray(
          recipe,
          mainState.cards
        );
        const checks = {
          cards: this.getUserRequairedRecipeCards(
            recipe,
            mainState.cards.completedCards
          ),
          energy: pickedCharacter.energy.quantity >= energy,
          cost: userBalance ? cost <= userBalance : false,
          buildingLevel: buiildingLevel
            ? buiildingLevel >= recipe.level
            : false,

          speciality: this.checkCharacterHaveNeededSpecialities(specialities),
          booster: booster.quantity >= 1,
          characterInUse: pickedCharacter.status === 'FREE',
        };
        const cardUI = mainState.cards.api.cards.find(
          (card: { id: number }) => card.id === recipe.result.cards[0].id
        );

        return {
          character: pickedCharacter,
          checks: checks,
          requaired: {
            specialities: specialities,
            energy: energy,
            cards: requairmentCardsUserHave,
            userBoostersQuantity: booster.quantity,
          },
          craftTime: craftTime,
          cost: cost,
          resourceMultipler: 1,
          useBooster: false,
          useComponent: false,
          recipeConfig: { ...recipe },
          boosterName: booster.name,
          cardUI: cardUI,
        };
      });

      return complepedRecipes;
    }
    return [];
  }
  setDefaultHomeBuildingValue(
    characterSlots: ICompletedCharacterSlots[],
    building: IBuildingHouse
  ): void {
    const buildings = this.craftingBuildings.getValue();
    const usedCharacters: (ICompletedCharacterSlots | undefined)[] =
      building.lastCharacterIdsUsed.map((characterId) =>
        characterSlots.find((slot) => slot.character?.id === characterId)
      );
    this.craftingBuildings.next({
      ...buildings,
      house: {
        building: building,
        lastSelectedCharactersSlot: usedCharacters,
      },
    });
  }
  setDefaultBuildingsValues(
    values: ICraftingBuilding,
    buildingName: craftBuildingNames
  ): void {
    const craftInProcess = values.building.resourceCrafts.filter(
      (craft) => craft.status === 'IN_PROCESS'
    )[0];

    const prevTimerValues = this.craftTimersInProcess.getValue();
    if (
      values.building.resourceCrafts.length > 0 &&
      values.slot?.character &&
      prevTimerValues !== null
    ) {
      this.addTaskTimer(
        craftInProcess,
        values.slot.character,
        buildingName,
        prevTimerValues
      );
    }
    this.craftingBuildings.next({
      ...this.craftingBuildings.getValue(),
      [buildingName]: values,
    });
  }
  getCompletedRecipeSpecialityRequaired(
    recipe: IRecipeConfigurations,
    character: ICharacterCompleted
  ): IComletedRecipeSpecialityRequairment[] {
    return recipe.required.specialities.map((specsRequaired) => {
      return {
        ...specsRequaired,
        userSpec: character.specialities.find(
          (specsUser) => specsRequaired.name === specsUser.config?.speciality
        ),
      };
    });
  }
  checkCharacterHaveNeededSpecialities(
    requairmentSpeciality: IComletedRecipeSpecialityRequairment[],
    full?: boolean
  ): any {
    const check = requairmentSpeciality.map((speciality) => {
      return {
        speciality: speciality,
        have:
          speciality.userSpec &&
          speciality.level <= speciality.userSpec.speciality.level,
      };
    });
    if (full) {
      return check;
    } else {
      return (
        check.filter((speciality) => speciality.have).length ===
        requairmentSpeciality.length
      );
    }
  }

  getCompletedRecipeEnergyRequaired(
    recipe: IRecipeConfigurations,
    character: ICharacterCompleted,
    multipler?: number
  ): number {
    let energy = recipe.required.energy;
    character.traitSlots
      .filter(
        (trait) =>
          trait.config?.effect.name === 'ENERGY_EXPENDED' &&
          trait.config.buildings.includes(recipe.building)
      )
      .forEach((trait) => {
        if (
          trait.config?.effect.quantity &&
          trait.config?.effect.quantity > 0
        ) {
          energy += trait.config.effect.quantity;
        }
      });

    character.skillSlots
      .filter(
        (skillSlot) =>
          skillSlot.skill.config?.effect.name === 'ENERGY_EXPENDED' &&
          skillSlot.skill.config?.buildings.includes(recipe.building) &&
          skillSlot.skill.config?.effect.chance === 1
      )
      .forEach((effect) => {
        if (
          effect.skill.config?.effect.quantity &&
          effect.skill.config?.effect.quantity > 0
        )
          energy += effect.skill.config.effect.quantity;
      });
    return multipler ? energy * multipler : energy;
  }
  getRecipeCost(
    recipe: IRecipeConfigurations,
    tax: { cityTax: number; systemTax: number },
    multipler: number
  ): number {
    let craftCost = recipe.cost * multipler;
    return (tax.cityTax + tax.systemTax) * craftCost;
  }
  getUserRequairedRecipeCardsArray(
    recipe: IRecipeConfigurations | IEnergyRecipeConfiguration,
    cardState: INewCardStateCompleted,
    multipler?: number
  ): { neededCardsQuantity: number; userHave: number }[] {
    return recipe.required.cards.map((requairedCard) => {
      const userHave = cardState.completedCards.filter(
        (userCard) =>
          (userCard.card.cardId === requairedCard.id &&
            userCard.card.status === 'ACTIVE') ||
          userCard.card.status === 'CREATED' ||
          userCard.card.status === 'EXCLUDED'
      ).length;

      const cardUI = cardState.api.cards.find(
        (card) => card.id === requairedCard.id
      );
      return {
        neededCardsQuantity: multipler
          ? requairedCard.quantity * multipler
          : requairedCard.quantity,
        userHave: userHave ? userHave : 0,
        cardUI: cardUI,
      };
    });
  }
  getUserRequairedRecipeCards(
    recipe: IRecipeConfigurations | IEnergyRecipeConfiguration,
    cardState: INewCompletedAccountCardsState[],
    multipler?: number
  ): boolean {
    return (
      recipe.required.cards
        .map((reqRecipeCard) => {
          const recipeRequairmentCardQuantity = multipler
            ? reqRecipeCard.quantity * multipler
            : reqRecipeCard.quantity;
          return (
            cardState.filter(
              (userResourceCard) =>
                userResourceCard.card.cardId === reqRecipeCard.id &&
                userResourceCard.card.status === 'ACTIVE'
            ).length >= recipeRequairmentCardQuantity
          );
        })
        .filter((res) => res).length >= recipe.required.cards.length
    );
  }

  getRecipeCraftTime(
    recipe: IRecipeConfigurations,
    character: ICharacterCompleted,
    multipler?: number
  ): number {
    let craftTime = recipe.time.max;
    character.traitSlots
      .filter(
        (traitSlot) =>
          traitSlot.config?.effect.name === 'TIME' &&
          traitSlot.config?.buildings.includes(recipe.building) &&
          traitSlot.config?.effect.chance === 1
      )
      .forEach((effect) => {
        if (effect.config?.effect.quantity)
          craftTime += recipe.time.max + effect.config?.effect.quantity;
      });
    character.specialities
      .filter((speciality) => speciality.config?.building === recipe.building)
      .forEach((specialities) => {
        const craftingSpeed = specialities.config?.levels.find(
          (levelConfig) => levelConfig.level === specialities.speciality.level
        )?.craftingSpeed;
        if (craftingSpeed) craftTime += recipe.time.max * craftingSpeed;
      });
    return multipler ? craftTime * multipler : craftTime;
  }
  getUserBooster(
    recipe: IRecipeConfigurations,
    userCards: INewCompletedAccountCardsState[]
  ): { quantity: number; name: string } {
    const booster = userCards.filter(
      (card) =>
        card.cardUI?.type === 'BOOSTER' &&
        card.card.status === 'ACTIVE' &&
        recipe.required.boosterCard.id === card.cardUI.id
    );
    return {
      quantity: booster.length,
      name:
        booster[0] && booster[0].cardUI?.appearance
          ? booster[0].cardUI.appearance.displayName
          : '',
    };
  }
  findCharacterInState(
    characterState: ICompletedCharacterSlots[],
    craftBuildingState: IBuildingCrafting,
    craftingType: ICraftTypes
  ): ICompletedCharacterSlots | null | undefined {
    const slot = characterState
      .filter((state) => state.character !== null)
      .find(
        (slot) =>
          slot &&
          slot.character?.id ===
            craftBuildingState[craftingType.typeName.building]?.slot?.character
              ?.id
      );
    return slot;
  }
  getCharacterEnergyInPercent(slot: ICompletedCharacterSlots): number {
    if (slot.character) {
      return (
        (slot.character.energy.quantity / slot.character.energy.capacity) * 100
      );
    } else {
      return 0;
    }
  }
  getResultCards(
    result: IPubResultUI,
    cardState: INewCardStateCompleted
  ): ICraftedCards[] {
    return result.cards.map((card) => {
      return {
        ui: cardState.api.cards.find((apiCard) => apiCard.id === card.cardId),
        quantity: card.quantity,
      };
    });
  }
  // getCraftedCards(
  //   craft: IBuildingCraft,
  //   completedCards: INewCardStateCompleted[]
  // ): { card: INewCompletedAccountCardsState; quantity: number }[] {
  //   let cards: any = [];
  //   const allCraftedCards = craft.craftedCardIds.concat(
  //     craft.additionalCraftedCardIds
  //   );
  //   allCraftedCards.forEach((card) => {
  //     if (!cards.includes(card)) {
  //       cards.push({
  //         card: card,
  //         quantity: allCraftedCards.filter((allCards) => allCards === card)
  //           .length,
  //       });
  //     }
  //   });
  //   return cards;
  // }
  getSpecialityProgress(
    specialityExp: number,
    nextLevelTotalExp: number,
    previewExp?: number
  ): number {
    const exp = previewExp ? previewExp + specialityExp : specialityExp;

    return Math.round((exp / nextLevelTotalExp) * 100);
  }
  updateBuildingCharacter(
    building: buildingsType,
    character?: ICharacterCompleted
  ): void {
    const oldBuildingsValues = this.craftingBuildings.getValue();
    const oldBuildingValue = oldBuildingsValues[building as craftBuildingNames];
    if (oldBuildingsValues && oldBuildingValue?.slot && character)
      this.craftingBuildings.next({
        ...oldBuildingsValues,
        [building]: {
          ...oldBuildingValue,
          slot: { ...oldBuildingValue.slot, character: character },
        },
      });
  }
  checkCraftType(
    recipeConfiguration: IRecipeConfigurations[],
    craft: IBuildingCraft
  ): recipeConfigurationType | undefined {
    return recipeConfiguration.find(
      (recipeConfig: IRecipeConfigurations) =>
        recipeConfig.id === craft.recipeConfigurationId
    )?.type;
  }
  accelerateTimer(
    craftTimersInProcessOld: IBuildingCraftingTimers,
    craftingBuildingName: craftBuildingNames,
    craft: IBuildingCraft
  ): void {
    let oldCraft = {
      ...craftTimersInProcessOld[craftingBuildingName],
      craft: craft,
    };
    clearInterval(oldCraft.timer);
    oldCraft.timer = setInterval(() => {
      const nestTime = craft.craftEndedTime - Date.now().valueOf();
      const progress = Math.abs(
        Math.round((nestTime / craft.craftTime) * 100) - 100
      );
      if (oldCraft.values) {
        oldCraft.values.progressTime =
          nestTime < 0
            ? '0'
            : nestTime < 3600000
            ? new Date(nestTime).toUTCString().slice(20, 25)
            : new Date(nestTime).toUTCString().slice(17, 25);

        oldCraft.values.taskProgress = nestTime > 0 ? progress : 0;
        oldCraft.values.timeInMs = nestTime;
        if (nestTime < 0) {
          clearInterval(oldCraft.timer);
          oldCraft = {
            ...oldCraft,
            craft: { ...oldCraft.craft, status: 'DONE' },
          };
          this.updateBuildingCraft(craftingBuildingName, craft);
        }
      }
      this.craftTimersInProcess.next({
        ...craftTimersInProcessOld,
        [craftingBuildingName]: { ...oldCraft },
      });
    }, 1000);
  }
  addTaskTimer(
    craft: IBuildingCraft,
    character: ICharacterCompleted,
    craftingBuildingName: craftBuildingNames,
    craftTimersInProcessOld: IBuildingCraftingTimers
  ): void {
    let newCraft: ICrafting | null = {
      timer: '',
      character: character,
      craft: craft,
      values: { taskProgress: 0, progressTime: '0', timeInMs: 0 },
    };
    this.craftingBuildings.next({
      ...this.craftingBuildings.getValue(),
      [craftingBuildingName]: {
        ...this.craftingBuildings.getValue()[craftingBuildingName],
        building: {
          ...this.craftingBuildings.getValue()[craftingBuildingName]?.building,
          lastCharacterIdUsed: craft.characterId,
        },
      },
    });
    newCraft.timer = setInterval(() => {
      const nestTime = craft.craftEndedTime - Date.now().valueOf();
      const progress = Math.abs(
        Math.round((nestTime / craft.craftTime) * 100) - 100
      );
      if (newCraft) {
        newCraft.values.progressTime =
          nestTime < 0
            ? '0'
            : nestTime < 3600000
            ? new Date(nestTime).toUTCString().slice(20, 25)
            : new Date(nestTime).toUTCString().slice(17, 25);

        newCraft.values.taskProgress = nestTime > 0 ? progress : 0;
        newCraft.values.timeInMs = nestTime;
        if (nestTime < 0) {
          this.updateCharacterStatus.next(true);

          clearInterval(newCraft.timer);
          newCraft = {
            ...newCraft,
            craft: { ...newCraft.craft, status: 'DONE' },
          };
        }
      }

      this.craftTimersInProcess.next({
        ...this.craftTimersInProcess.getValue(),
        [craftingBuildingName]: { ...newCraft },
      });
    }, 1000);
  }

  public setCharacter(
    character: ICharacterCompleted,
    expirience: ICraftCharacterExperience[],
    craftingType?: ICraftTypes,
    neededSpecialities?: specialityType[],
    isMomentalOperation?: boolean
  ): ICharacterCompletedWithPreviewSpecialityExp {
    let specialities: ICompletedCharacterSpeciality[] = [];
    if (craftingType) {
      specialities = character.specialities.filter(
        (speciality) =>
          craftingType.typeName.system &&
          speciality.config?.recipeBuildings.includes(
            craftingType.typeName.system
          )
      );
    } else if (neededSpecialities) {
      specialities = character.specialities.filter((speciality) =>
        speciality.config
          ? neededSpecialities.includes(speciality.config?.speciality)
          : false
      );
    }
    return {
      ...character,
      specialities: specialities
        .map((speciality) => {
          const newSpecialityExp = expirience.find(
            (expirience) =>
              expirience.speciality === speciality.config?.speciality
          )?.quantity;
          const levelConfigs = speciality.config
            ? speciality.config.levels.find(
                (levels) => levels.level === speciality.speciality.level + 1
              )?.expTotal
            : 0;

          let specialityProgress = 0;
          let newSpecialityProgress = 0;
          if (newSpecialityExp && levelConfigs) {
            specialityProgress = this.getSpecialityProgress(
              isMomentalOperation
                ? speciality.speciality.exp - newSpecialityExp
                : speciality.speciality.exp,
              levelConfigs
            );
            newSpecialityProgress = this.getSpecialityProgress(
              isMomentalOperation
                ? speciality.speciality.exp - newSpecialityExp
                : speciality.speciality.exp,
              levelConfigs,
              newSpecialityExp
            );
          }
          return {
            ...speciality,
            progress: specialityProgress,
            newExp: newSpecialityExp ? Math.floor(newSpecialityExp) : undefined,
            newProgress: newSpecialityProgress,
          };
        })
        .filter((res) => res.newExp && res.newExp > 0),
    };
  }

  updateBuildingCraft(
    craftingBuildingName: craftBuildingNames,
    craft: IBuildingCraft
  ): void {
    if (
      craft.craftEndedTime !==
      this.craftingBuildings.getValue()[craftingBuildingName]?.building
        .resourceCrafts[0].craftEndedTime
    ) {
      this.craftingBuildings.next({
        ...this.craftingBuildings.getValue(),
        [craftingBuildingName]: {
          ...this.craftingBuildings.getValue()[craftingBuildingName],
          building: {
            ...this.craftingBuildings.getValue()[craftingBuildingName]
              ?.building,
            resourceCrafts: [craft],
          },
        },
      });
    }
  }
}
